import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import firebase from '../db/firebase/firebase';
import IconPoint from '../assets/icons/icon-point.svg';
import { motion, AnimatePresence } from 'framer-motion';

const DEFAULT_PHOTO = 'https://t4.ftcdn.net/jpg/00/64/67/63/360_F_64676383_LdbmhiNM6Ypzb3FM4PPuFP9rHe7ri8Ju.webp';

const Users = () => {

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [pointsToAdd, setPointsToAdd] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [updatingPoints, setUpdatingPoints] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const VALID_PASSWORD = 'admin4532'; 

  useEffect(() => {
    const usersCol = collection(firebase.db, 'userData');
    const unsubscribe = onSnapshot(
      usersCol,
      snapshot => {
        const list = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
        setUsers(list);
        setLoading(false);
      },
      err => {
        console.error('Error fetching users:', err);
        setError('Failed to load users.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setPointsToAdd('');
    setConfirming(false);
  };

  const handleAddPoints = async () => {
    if (!selectedUser || isNaN(pointsToAdd)) return;
    try {
      setUpdatingPoints(true);
      const ref = doc(firebase.db, 'userData', selectedUser.id);
      const newPoints = (selectedUser.points || 0) + parseInt(pointsToAdd);

      await updateDoc(ref, { points: newPoints });

      await addDoc(collection(firebase.db, 'pointsHistory'), {
        userId: selectedUser.id,
        email: selectedUser.email,
        pointsBefore: selectedUser.points || 0,
        pointsAdded: parseInt(pointsToAdd),
        pointsAfter: newPoints,
        createdAt: serverTimestamp(),
      });

      setConfirming(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error updating points:', err);
    } finally {
      setUpdatingPoints(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-white z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-gray-800"></div>
      </div>
    );
  }

  if (error) {
    return <p className="text-center text-red-500">{error}</p>;
  }

  return (
    <div className="flex flex-col flex-1 h-screen bg-gray-300">
      <header className="bg-white shadow flex justify-between items-center px-6 py-4 z-20">
        <h1 className="text-2xl font-bold text-center">Users</h1>
        <div className=" flex justify-center">
          <input
            type="text"
            placeholder="Search by name or code"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full max-w-md py-2 self-center px-6 border placeholder-white bg-[#476C73] text-white border-gray-300 rounded-[20px] focus:outline-none focus:ring focus:border-blue-300"
          />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {filteredUsers.length === 0 ? (
          <p className="text-center text-gray-500">No users found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map(user => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="relative bg-white px-4 pt-8 pb-4 rounded-lg shadow-md"
              >
                <button
                  onClick={() => handleUserClick(user)}
                  className="absolute top-3 right-3 bg-[#6496A4] hover:bg-[#49b4b4] text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
                >
                  Add Point
                  <img src={IconPoint} alt="Add Point Icon" className="h-4 w-4 ml-1" />
                </button>

                <div className="flex items-center">
                  <img
                    src={user.photo || DEFAULT_PHOTO}
                    alt={user.name || 'User avatar'}
                    className="h-16 w-16 rounded-full mr-4 object-cover"
                  />
                  <div >
                    <h3 className="text-lg font-semibold">{user.name || 'No name'}</h3>
                    <p className="text-sm text-gray-600 font-bold">{user.email}</p>
                    <p className="text-sm text-gray-600">
                      Points: <span className="font-bold text-[#5298A8]">{user.points}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Code: <span className="font-bold">{user.code}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1 ">
                      Created:{' '}
                      {user.createdAt?.seconds
                        ? new Date(user.createdAt.seconds * 1000).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : 'No date'}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {selectedUser && (
        <AnimatePresence>
          <motion.div
            className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-[#0D2C33] p-8 rounded-[40px] w-full max-w-lg flex flex-col items-center relative"
            >
              <button
                onClick={() => {
                  if (!updatingPoints) {
                    setConfirming(false);
                    setSelectedUser(null);
                    setAdminPassword('');
                    setPasswordError('');
                  }
                }}
                className="absolute top-2 right-5 text-white text-2xl font-bold hover:text-gray-300"
                aria-label="Close"
              >
                ×
              </button>

              <h2 className="text-white text-center text-lg font-semibold mb-6">
                How many points do you want to add<br />to this account?
              </h2>

              {selectedUser && pointsToAdd !== '' && !isNaN(pointsToAdd) && (
                <p className="text-white font-semibold mb-4 text-lg">
                  {selectedUser.points || 0} + {parseInt(pointsToAdd)} ={' '}
                  <span className="text-[#5CC8C8]">
                    {(selectedUser.points || 0) + parseInt(pointsToAdd)} points
                  </span>
                </p>
              )}

              <input
                type="number"
                value={pointsToAdd}
                onChange={e => setPointsToAdd(e.target.value)}
                placeholder="Enter number here"
                disabled={updatingPoints}
                className="rounded-full w-full py-3 px-6 text-center bg-[#D9D9D9] text-[#3C3C3C] text-lg font-semibold outline-none mb-4 disabled:opacity-60"
              />

              {!confirming ? (
                <button
                  onClick={() => setConfirming(true)}
                  disabled={updatingPoints}
                  className="bg-[#5CC8C8] px-10 py-2 rounded-full text-white font-semibold disabled:opacity-60"
                >
                  OK
                </button>
              ) : (
                <>
                  <input
                    type="password"
                    placeholder="Enter admin password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    disabled={updatingPoints}
                    className="rounded-full w-full py-3 px-6 text-center bg-white text-black text-lg font-semibold outline-none mb-2 disabled:opacity-60"
                  />
                  {passwordError && <p className="text-red-400 text-sm mb-2">{passwordError}</p>}

                  <div className="flex gap-4 mt-2">
                    <button
                      onClick={() => {
                        if (adminPassword === VALID_PASSWORD) {
                          handleAddPoints();
                          setPasswordError('');
                        } else {
                          setPasswordError('Incorrect password');
                        }
                      }}
                      disabled={updatingPoints}
                      className="bg-green-600 px-6 py-2 rounded-full text-white font-semibold flex items-center justify-center disabled:opacity-60 min-w-[100px]"
                    >
                      {updatingPoints ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        'Confirm'
                      )}
                    </button>

                    <button
                      onClick={() => {
                        if (!updatingPoints) {
                          setConfirming(false);
                          setSelectedUser(null);
                          setAdminPassword('');
                          setPasswordError('');
                        }
                      }}
                      disabled={updatingPoints}
                      className="bg-red-500 px-6 py-2 rounded-full text-white font-semibold disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default Users;